<script lang="ts" setup>
import { i18n } from '@/i18n';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { useUnmatchedOrderStore } from '../pages/useUnmatchedOrderStore';
import type { UnmatchedOrderItem } from '../domains/entities/UnmatchedOrderItem';
import { DateService } from '@/coreDomainsModule/services/dateService';
import OrderStatusBadge from '@/corePagesModule/components/OrderStatusBadge.vue';

const props = defineProps<{
  modalState: boolean;
  checkedItems: UnmatchedOrderItem[];
}>();

const emits = defineEmits<{
  (e: 'canceled'): void;
  (e: 'confirm'): void;
}>();

const t = i18n.t;
const store = useUnmatchedOrderStore();
const { state, filteredExistingOrders, selectedExistingOrder } = storeToRefs(store);

const existingOrderColumns = [
  '32px',
  '120px',
  '120px',
  '120px',
  '120px',
  '120px',
  '120px',
  '80px',
  '120px',
  '120px',
  '120px',
  '120px',
  '120px',
  '80px',
  '120px',
];

const existingOrderTableLoadingStatus = computed(() => {
  if (filteredExistingOrders.value.length === 0 && state.value.matchSearchKeyword) {
    return { type: 'zero-match' as const };
  }
  return { type: 'success' as const };
});

const isConfirmDisabled = computed(() => !state.value.selectedExistingOrderId);
</script>

<template>
  <InfoModal
    :modal-state="modalState"
    :modal-width="1280"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('突合する')"
    :is-disabled-next-button="isConfirmDisabled"
    :enable-outside-click="true"
    :is-disabled-scroll="true"
    @on-back="emits('canceled')"
    @on-next="emits('confirm')"
  >
    <template #title>{{ t('既存発注との突合') }}</template>
    <template #description>
      {{ t('選択した') }}{{ checkedItems.length }}{{ t('件の未照合データを既存発注と照合します。突合先の発注を選択してください。') }}
    </template>

    <div class="match-modal">
      <!-- 選択中の未照合データの概要 -->
      <div class="match-modal__section">
        <div class="match-modal__section-title">{{ t('未照合データ（選択中）') }}</div>
        <div class="match-modal__selected-summary">
          <div v-for="item in checkedItems" :key="item.unmatchedOrderId" class="match-modal__selected-item">
            <span class="match-modal__selected-item-value">{{ t('置場コード') }}{{ ':\n' }}{{ item.warehouseNumber || '--' }}</span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-label">{{ t('置場名') }}{{ ':\n' }}{{ item.warehouse || '--' }}</span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-value">{{ t('仕入先コード') }}{{ ':\n' }}{{ item.supplierNumber || '--' }}</span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-label">{{ t('仕入先名') }}{{ ':\n' }}{{ item.supplierName || '--' }}</span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-value">{{ t('商品コード') }}{{ ':\n' }}{{ item.businessNumber || '--' }}</span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-label">{{ t('商品名') }}{{ ':\n' }}{{ item.businessName || '--' }}</span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-value">{{ t('容量') }}{{ ':\n' }}{{ item.package || '--' }}</span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-value">{{ t('発注番号') }}{{ ':\n' }}{{ item.orderNumber || '--' }}</span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-value">
              {{ t('発注作成日') }}
              {{ ':\n' }}
              {{ item.createdOrderDate ? DateService.convertMSUnixTimeToYYYYMMDDSlash(item.createdOrderDate) : '--' }}
            </span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-value">
              {{ t('希望納期') }}
              {{ ':\n' }}
              {{ item.desiredDeliveryDate ? DateService.convertMSUnixTimeToYYYYMMDDSlash(item.desiredDeliveryDate) : '--' }}
            </span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-value"
              >{{ t('納期') }}
              {{ ':\n' }}
              {{ item.estimatedTimeOfArrival ? DateService.convertMSUnixTimeToYYYYMMDDSlash(item.estimatedTimeOfArrival) : '--' }}
            </span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-value">{{ t('発注数量') }}{{ ':\n' }}{{ item.orderAmount || '--' }}</span>
            <span class="match-modal__selected-item-separator">/</span>
            <span class="match-modal__selected-item-value">
              {{ t('ステータス') }}{{ ':\n' }}<OrderStatusBadge :order-status-str="item.orderStatus" />
            </span>
          </div>
        </div>
      </div>

      <!-- 検索 + 既存発注テーブル -->
      <div class="match-modal__section">
        <div class="match-modal__section-title">{{ t('突合先の既存発注を選択') }}</div>
        <div class="match-modal__search">
          <input
            v-model="state.matchSearchKeyword"
            type="text"
            class="match-modal__search-input"
            :placeholder="t('仕入先名・商品名・発注番号で検索')"
          />
        </div>

        <div class="match-modal__table-wrapper">
          <EzTable
            :grid-template-columns="existingOrderColumns"
            :loading-status="existingOrderTableLoadingStatus"
            sticky-header
            max-height="300px"
          >
            <template #header>
              <EzHeader force-center>&nbsp;</EzHeader>
              <EzHeader>{{ t('倉庫CD') }}</EzHeader>
              <EzHeader>{{ t('倉庫名') }}</EzHeader>
              <EzHeader>{{ t('仕入先CD') }}</EzHeader>
              <EzHeader>{{ t('仕入先名') }}</EzHeader>
              <EzHeader>{{ t('商品CD') }}</EzHeader>
              <EzHeader>{{ t('商品名') }}</EzHeader>
              <EzHeader>{{ t('容量') }}</EzHeader>
              <EzHeader>{{ t('発注管理番号') }}</EzHeader>
              <EzHeader>{{ t('発注番号') }}</EzHeader>
              <EzHeader>{{ t('発注作成日') }}</EzHeader>
              <EzHeader>{{ t('希望納期') }}</EzHeader>
              <EzHeader>{{ t('納期') }}</EzHeader>
              <EzHeader>{{ t('発注数量') }}</EzHeader>
              <EzHeader>{{ t('ステータス') }}</EzHeader>
            </template>
            <template #body>
              <EzTableRow
                v-for="order in filteredExistingOrders"
                :key="order.orderId"
                :grid-template-columns="existingOrderColumns"
                :is-highlighted="state.selectedExistingOrderId === order.orderId"
                class="match-modal__order-row"
                @click="store.selectExistingOrder(order.orderId)"
              >
                <EzCell force-center class="match-modal__radio-cell">
                  <input
                    type="radio"
                    class="match-modal__radio"
                    :checked="state.selectedExistingOrderId === order.orderId"
                    :value="order.orderId"
                    name="existingOrder"
                    @change="store.selectExistingOrder(order.orderId)"
                  />
                </EzCell>
                <EzCell>{{ order.warehouseNumber }}</EzCell>
                <EzCell>{{ order.warehouse }}</EzCell>
                <EzCell>{{ order.supplierNumber }}</EzCell>
                <EzCell>{{ order.supplierName }}</EzCell>
                <EzCell>{{ order.businessNumber }}</EzCell>
                <EzCell>{{ order.businessName }}</EzCell>
                <EzCell is-number>{{ order.package }}</EzCell>
                <EzCell>{{ order.orderIssuedNumber || '--' }}</EzCell>
                <EzCell>{{ order.orderNumber || '--' }}</EzCell>
                <EzCell>{{ order.createdOrderDate ? DateService.convertMSUnixTimeToYYYYMMDDSlash(order.createdOrderDate) : '--' }}</EzCell>
                <EzCell>{{ DateService.convertMSUnixTimeToYYYYMMDDSlash(order.desiredDeliveryDate) }}</EzCell>
                <EzCell>{{ DateService.convertMSUnixTimeToYYYYMMDDSlash(order.estimatedTimeOfArrival) }}</EzCell>
                <EzCell is-number>{{ order.orderAmount.addComma() }}</EzCell>
                <EzCell><OrderStatusBadge :order-status-str="order.orderStatus" /></EzCell>
              </EzTableRow>
            </template>
          </EzTable>
        </div>
      </div>

      <!-- 反映プレビュー -->
      <div v-if="selectedExistingOrder" class="match-modal__section">
        <div class="match-modal__section-title">{{ t('反映内容プレビュー') }}</div>
        <div class="match-modal__preview">
          <div class="match-modal__preview-row">
            <span class="match-modal__preview-label">{{ t('発注番号') }}</span>
            <span class="match-modal__preview-before">{{ selectedExistingOrder.orderNumber || '--' }}</span>
            <span v-if="checkedItems[0]?.orderNumber !== selectedExistingOrder.orderNumber" class="match-modal__preview-arrow">&rarr;</span>
            <span v-if="checkedItems[0]?.orderNumber !== selectedExistingOrder.orderNumber" class="match-modal__preview-after">
              {{ checkedItems[0]?.orderNumber || '--' }}
            </span>
          </div>

          <div class="match-modal__preview-row">
            <span class="match-modal__preview-label">{{ t('作成日') }}</span>
            <span class="match-modal__preview-before">
              {{
                selectedExistingOrder.createdOrderDate
                  ? DateService.convertMSUnixTimeToYYYYMMDDSlash(selectedExistingOrder.createdOrderDate)
                  : '--'
              }}
            </span>
            <span
              v-if="checkedItems[0]?.createdOrderDate && checkedItems[0]?.createdOrderDate !== selectedExistingOrder.createdOrderDate"
              class="match-modal__preview-arrow"
            >
              &rarr;
            </span>
            <span
              v-if="checkedItems[0]?.createdOrderDate && checkedItems[0]?.createdOrderDate !== selectedExistingOrder.createdOrderDate"
              class="match-modal__preview-after"
            >
              {{ DateService.convertMSUnixTimeToYYYYMMDDSlash(checkedItems[0]?.createdOrderDate) }}
            </span>
          </div>

          <div class="match-modal__preview-row">
            <span class="match-modal__preview-label">{{ t('希望納期') }}</span>
            <span class="match-modal__preview-before">
              {{ DateService.convertMSUnixTimeToYYYYMMDDSlash(selectedExistingOrder.desiredDeliveryDate) }}
            </span>
            <span
              v-if="
                checkedItems[0]?.desiredDeliveryDate && checkedItems[0]?.desiredDeliveryDate !== selectedExistingOrder.desiredDeliveryDate
              "
              class="match-modal__preview-arrow"
            >
              &rarr;
            </span>
            <span
              v-if="
                checkedItems[0]?.desiredDeliveryDate && checkedItems[0]?.desiredDeliveryDate !== selectedExistingOrder.desiredDeliveryDate
              "
              class="match-modal__preview-after"
            >
              {{ DateService.convertMSUnixTimeToYYYYMMDDSlash(checkedItems[0]?.desiredDeliveryDate) }}
            </span>
          </div>

          <div class="match-modal__preview-row">
            <span class="match-modal__preview-label">{{ t('納期') }}</span>
            <span class="match-modal__preview-before">
              {{ DateService.convertMSUnixTimeToYYYYMMDDSlash(selectedExistingOrder.estimatedTimeOfArrival) }}
            </span>
            <span
              v-if="
                checkedItems[0]?.estimatedTimeOfArrival &&
                checkedItems[0]?.estimatedTimeOfArrival !== selectedExistingOrder.estimatedTimeOfArrival
              "
              class="match-modal__preview-arrow"
            >
              &rarr;
            </span>
            <span
              v-if="
                checkedItems[0]?.estimatedTimeOfArrival &&
                checkedItems[0]?.estimatedTimeOfArrival !== selectedExistingOrder.estimatedTimeOfArrival
              "
              class="match-modal__preview-after"
            >
              {{ DateService.convertMSUnixTimeToYYYYMMDDSlash(checkedItems[0].estimatedTimeOfArrival) }}
            </span>
          </div>

          <div class="match-modal__preview-row">
            <span class="match-modal__preview-label">{{ t('発注数量') }}</span>
            <span class="match-modal__preview-before">{{ selectedExistingOrder.orderAmount }}</span>
            <span
              v-if="checkedItems[0]?.orderAmount && checkedItems[0]?.orderAmount !== selectedExistingOrder.orderAmount"
              class="match-modal__preview-arrow"
            >
              &rarr;
            </span>
            <span
              v-if="checkedItems[0]?.orderAmount && checkedItems[0]?.orderAmount !== selectedExistingOrder.orderAmount"
              class="match-modal__preview-after"
            >
              {{ checkedItems[0]?.orderAmount }}
            </span>
          </div>

          <div class="match-modal__preview-row">
            <span class="match-modal__preview-label">{{ t('発注ステータス') }}</span>
            <span class="match-modal__preview-before"><OrderStatusBadge :order-status-str="selectedExistingOrder.orderStatus" /></span>
            <span
              v-if="checkedItems[0]?.orderStatus && checkedItems[0]?.orderStatus !== selectedExistingOrder.orderStatus.toString()"
              class="match-modal__preview-arrow"
            >
              &rarr;
            </span>
            <span
              v-if="checkedItems[0]?.orderStatus && checkedItems[0]?.orderStatus !== selectedExistingOrder.orderStatus.toString()"
              class="match-modal__preview-after"
            >
              <OrderStatusBadge :order-status-str="checkedItems[0]?.orderStatus" />
            </span>
          </div>
        </div>
      </div>
    </div>
  </InfoModal>
</template>

<style lang="scss" scoped>
.match-modal {
  display: grid;
  gap: 20px;

  &__section {
    display: grid;
    gap: 8px;
  }

  &__section-title {
    font-size: 14px;
    font-weight: 700;
    color: $textDefault;
    line-height: 20px;
  }

  &__selected-summary {
    display: grid;
    gap: 4px;
    padding: 12px;
    background-color: $backgroundDefault;
    border-radius: 8px;
  }

  &__selected-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    line-height: 18px;
    color: $textDefault;
    white-space: pre-line;
  }

  &__selected-item-label {
    font-weight: 600;
  }

  &__selected-item-value {
    color: $textSecondary;
  }

  &__selected-item-separator {
    color: $borderSecondary;
  }

  &__search {
    display: grid;
  }

  &__search-input {
    height: 36px;
    padding: 0 12px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    font-size: 14px;
    color: $textDefault;
    outline: none;

    &::placeholder {
      color: $textSecondary;
    }

    &:focus {
      border-color: $semanticPrimary;
    }
  }

  &__table-wrapper {
    border: 1px solid $borderDefault;
    border-radius: 4px;
    overflow: hidden;

    :deep(.table__header) {
      min-height: 36px;
    }

    :deep(.table__header__content) {
      line-height: 36px;
      font-size: 13px;
    }

    :deep(.table__cell) {
      border-bottom: 1px solid $surfaceSecondary;
    }

    :deep(.table__cell__content) {
      padding-top: 4px;
      padding-bottom: 4px;
      font-size: 13px;
    }
  }

  &__order-row {
    cursor: pointer;

    &:hover :deep(.table__cell) {
      background-color: $backgroundDefault;
    }
  }

  &__radio {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: $semanticPrimary;
  }

  &__radio-cell {
    :deep(.table__cell__content) {
      overflow: visible;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  &__preview {
    display: grid;
    gap: 8px;
    padding: 12px 16px;
    background-color: $semanticPrimarySubdued;
    border-radius: 8px;
    border: 1px solid $semanticPrimary;
  }

  &__preview-row {
    display: grid;
    grid-template-columns: max-content 1fr auto 1fr;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    line-height: 18px;
  }

  &__preview-label {
    font-weight: 700;
    color: $textDefault;
  }

  &__preview-before {
    color: $textSecondary;
    text-align: right;
    justify-self: end;
  }

  &__preview-arrow {
    color: $semanticPrimary;
    font-weight: 700;
    text-align: center;
  }

  &__preview-after {
    color: $semanticPrimary;
    font-weight: 700;
    justify-self: start;
  }
}
</style>
